import emptyBeach from '@/assets/images/empty-beach.png';
import emptyField2 from '@/assets/images/empty-field2.png';
import { constants } from '@/config';
import { BRAND } from '@/models/auth/permissions.enum';

export const CHANNELS = Object.freeze({
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_UGC: 'INSTAGRAM_UGC',
  FACEBOOK: 'FACEBOOK',
  X: 'X',
  PINTEREST: 'PINTEREST',
  YOUTUBE: 'YOUTUBE',
  LINKEDIN: 'LINKEDIN',
  TIKTOK: 'TIKTOK',
  TIKTOK_UGC: 'TIKTOK_UGC',
  THREADS: 'THREADS',
});

export const OWNED_CHANNELS = [
  CHANNELS.INSTAGRAM,
  CHANNELS.FACEBOOK,
  CHANNELS.X,
  CHANNELS.PINTEREST,
  CHANNELS.YOUTUBE,
  CHANNELS.LINKEDIN,
  CHANNELS.TIKTOK,
  CHANNELS.THREADS,
];

export const OWNED_CHANNELS_V2 = [
  CHANNELS.INSTAGRAM,
  CHANNELS.TIKTOK,
  CHANNELS.FACEBOOK,
  CHANNELS.PINTEREST,
  CHANNELS.YOUTUBE,
  CHANNELS.LINKEDIN,
  CHANNELS.THREADS,
  CHANNELS.X,
];
export const CHANNEL_TO_SUPPORTED_BOARD = {
  [CHANNELS.INSTAGRAM]: constants.INSTAGRAM,
  [CHANNELS.INSTAGRAM_UGC]: constants.INSTAGRAM,
  [CHANNELS.FACEBOOK]: constants.FACEBOOK,
  [CHANNELS.X]: constants.TWITTER,
  [CHANNELS.PINTEREST]: constants.PINTEREST,
  [CHANNELS.TIKTOK]: constants.TIKTOK,
  [CHANNELS.YOUTUBE]: constants.YOUTUBE,
  [CHANNELS.LINKEDIN]: constants.LINKEDIN,
  [CHANNELS.THREADS]: constants.THREADS,
};

export const CHANNEL_TO_BOARD_PERMISSION = {
  [CHANNELS.INSTAGRAM]: BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM,
  [CHANNELS.INSTAGRAM_UGC]: BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM,
  [CHANNELS.FACEBOOK]: BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS,
  [CHANNELS.X]: BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS,
  [CHANNELS.PINTEREST]: BRAND.PINTEREST.CAN_ACCESS_PINT,
  [CHANNELS.TIKTOK]: BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
  [CHANNELS.YOUTUBE]: BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS,
  [CHANNELS.LINKEDIN]: BRAND.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS,
  [CHANNELS.THREADS]: BRAND.THREADS.CAN_ACCESS_THREADS_INSIGHTS,
};

export const UGC_CHANNELS = [CHANNELS.INSTAGRAM_UGC, CHANNELS.TIKTOK_UGC];

export const CHANNEL_ICON_NAME = Object.freeze({
  [CHANNELS.INSTAGRAM]: 'instagram',
  [CHANNELS.INSTAGRAM_UGC]: 'instagram',
  [CHANNELS.FACEBOOK]: 'facebook',
  [CHANNELS.X]: 'twitter',
  [CHANNELS.PINTEREST]: 'pinterest',
  [CHANNELS.YOUTUBE]: 'youtube',
  [CHANNELS.LINKEDIN]: 'linkedin',
  [CHANNELS.TIKTOK]: 'tiktok',
  [CHANNELS.TIKTOK_UGC]: 'tiktok',
  [CHANNELS.THREADS]: 'threads',
});

export const CHANNEL_LABEL = Object.freeze({
  [CHANNELS.INSTAGRAM]: 'Instagram',
  [CHANNELS.INSTAGRAM_UGC]: 'Instagram UGC',
  [CHANNELS.FACEBOOK]: 'Facebook',
  [CHANNELS.X]: 'X',
  [CHANNELS.PINTEREST]: 'Pinterest',
  [CHANNELS.YOUTUBE]: 'YouTube',
  [CHANNELS.LINKEDIN]: 'LinkedIn',
  [CHANNELS.TIKTOK]: 'TikTok',
  [CHANNELS.TIKTOK_UGC]: 'TikTok UGC',
  [CHANNELS.THREADS]: 'Threads',
});

export const CHANNEL_SECTION_TOOLTIP = 'Applies to all media types for the selected channels';

export function getChannelColorIcon(channel) {
  const label = CHANNEL_LABEL[channel] ?? channel;
  return `${label.replace(' UGC', '').toLowerCase()}-color`;
}

export const ACTION_TYPE = Object.freeze({
  CONTENT_TAGS: 'contentTags',
  GALLERIES: 'galleries',
  CAMPAIGNS: 'campaigns',
  BOARDS: 'boards',
});

export const FILTER_TYPE = Object.freeze({
  KEYWORD: 'keyword',
  DATE_PUBLISHED: 'date_published',
  PROMPT: 'prompt',
});

export const FILTER_OPERATOR = Object.freeze({
  OR: 'OR',
  AND: 'AND',
});

export const RULES_BUILDER_CHIP_TYPE = Object.freeze({
  FILTER: 'FILTER',
  ACTION: 'ACTION',
});

export const invalidContentTagInputTooltip =
  'You must name your new Content Tag and select at least one brand';
export const noBrandAccessTooltip = 'You don’t have access to some brands in this automation.';
export const noEditContentAutomationPermissionsTooltip =
  'You don’t have access to edit content automations. Please contact your plan administrator to enable this permission.';

export const applyToExistingPostSuccessMessage =
  'Your automation has been saved. It may take a few moments to apply it to all posts.';
export const disableContentAutomationSuccessMessage = 'Your automation has been paused.';
export const disableContentAutomationFailedMessage =
  'Failed to pause the automation. Please try again.';
export const runContentAutomationFailedMessage = 'Failed to run the automation. Please try again.';

export const deleteAutomationConfirmationTitle = 'Delete Content Automation?';
export const deleteAutomationConfirmationMessage =
  'This will permanently remove this content automation. Any content previously impacted by this automation will remain unchanged.';
export const deleteConfirmationAlias = 'Delete';
export const deleteAutomationUnauthorizedError = (contentAutomationName) =>
  `You do not have permission to delete content automation ${contentAutomationName}.`;
export const deleteAutomationNotFoundError = (contentAutomationName) =>
  `Content automation ${contentAutomationName} was not found.`;
export const deleteAutomationUnknownError = (contentAutomationName) =>
  `Unable to delete content automation ${contentAutomationName} at this time, please try again later.`;
export const deleteAutomationSuccessMessage = (contentAutomationName) =>
  `Content automation ${contentAutomationName} successfully deleted`;

export const contentAutomationListContainsErrorMessage =
  'One or more of your automations below has an error and is not running. Edit or delete this automation to resolve the issue.';

export const contentAutomationListItemContainsErrorMessage = 'This automation has errors';

export const contentAutomationActionErrorMessages = Object.freeze({
  [ACTION_TYPE.CONTENT_TAGS]:
    'This Content Tag was deleted or is not associated with any of the brands you have selected for this automation',
  [ACTION_TYPE.BOARDS]:
    'This Board was deleted or is not associated with any of the brands you have selected for this automation',
  [ACTION_TYPE.GALLERIES]:
    'This Gallery was deleted or is not associated with any of the brands you have selected for this automation',
  [ACTION_TYPE.CAMPAIGNS]:
    'This Campaign was deleted or is not associated with any of the brands you have selected for this automation',
});

export const deleteFilterGroupConfirmationTitle = 'Delete Container?';
export const deleteFilterGroupConfirmationMessage =
  'Are you sure you want to delete this container? You can’t undo this action.';
export const deleteFilterGroupConfirmationAlias = 'Delete Container';

export const CONTENT_AUTOMATION_WIZARD_NEXT_BUTTON_TOOLTIPS = [
  'You must specify which brands will have their content monitored',
  'You must specify which social channels will have their content monitored',
  'You must specify a condition and a valid action',
];

export const noAutomationsEmptyState = Object.freeze({
  image: emptyField2,
  title: "You don't have any content automations yet",
  action: 'Create Automation',
});

export const filterEmptyState = Object.freeze({
  image: emptyBeach,
  title: 'No content automations found',
  message: 'Adjust search and try again.',
});

export const errorGetContentAutomationsEmptyState = Object.freeze({
  image: emptyField2,
  title: 'Try Again',
  message:
    'There was a delay in loading your content automations. Please refresh the page and try again.',
});

export const saveAndApplyTitle = 'Save and Apply to Existing Posts';
export const saveAndApplyBody = (counted) =>
  `This will start running the automation and apply the rule to the existing ${counted} posts that match the defined criteria`;
export const saveAndApplyConfirmationAlias = 'Continue';
export const saveWithoutApplyTitle = 'Save Rule';
export const saveWithoutApplyBody =
  'This will start running the automation on new posts only. Existing posts will not be affected.';
export const saveWithoutApplyConfirmationAlias = 'Save';
export const cancelEditConfirmationTitle = 'Discard Changes?';
export const cancelEditConfirmationMessage = 'If you exit now, your changes will be discarded.';
export const cancelEditConfirmationAlias = 'Discard';

export const createContentAutomationPanelTitle = 'Create a Rule - Owned / UGC';
export const editContentAutomationPanelTitle = 'Edit Rule - Owned / UGC';
export const editContentAutomationActionErrorMessage =
  'This criteria is no longer valid. Please remove it to save this rule.';

export const selectChannelOptions = [
  {
    value: CHANNELS.FACEBOOK,
    icon: CHANNELS.FACEBOOK.toLocaleLowerCase(),
    label: 'Facebook',
  },
  {
    value: CHANNELS.INSTAGRAM,
    icon: CHANNELS.INSTAGRAM.toLocaleLowerCase(),
    label: 'Instagram',
  },
  {
    value: CHANNELS.LINKEDIN,
    icon: CHANNELS.LINKEDIN.toLocaleLowerCase(),
    label: 'LinkedIn',
  },
  {
    value: CHANNELS.PINTEREST,
    icon: CHANNELS.PINTEREST.toLocaleLowerCase(),
    label: 'Pinterest',
  },
  {
    value: CHANNELS.THREADS,
    icon: CHANNELS.THREADS.toLocaleLowerCase(),
    label: 'Threads',
  },
  {
    value: CHANNELS.TIKTOK,
    icon: CHANNELS.TIKTOK.toLocaleLowerCase(),
    label: 'TikTok',
  },
  {
    value: CHANNELS.X,
    icon: 'twitter',
    label: 'X',
  },
  {
    value: CHANNELS.YOUTUBE,
    icon: CHANNELS.YOUTUBE.toLocaleLowerCase(),
    label: 'YouTube',
  },
  {
    value: CHANNELS.INSTAGRAM_UGC,
    icon: 'instagram',
    label: 'Instagram',
    name: 'Instagram UGC',
  },
  {
    value: CHANNELS.TIKTOK_UGC,
    icon: 'tiktok',
    label: 'TikTok',
    name: 'TikTok UGC',
  },
];

export const CONTENT_AUTOMATION_SUBHEADER_MESSAGE =
  'You can create rules to add content tags to posts or add published posts to Campaigns, based on specific conditions, such as channel, captions and date published.';

export const PANEL_NAMES = Object.freeze({
  BRANDS: 'brands',
  CHANNELS: 'channels',
  RULES: 'rules',
  NAME_AND_SAVE: 'nameAndSave',
});

export const CONTENT_AUTOMATION_PAGE_SIZE = 8;

export const CONTENT_AUTOMATION_LEARN_MORE_URL =
  'https://help.dashsocial.com/hc/en-us/articles/31114279045389-Managing-Content-Automation';

export const CONTENT_AUTOMATION_EDIT_PERMISSION_REQUIRED_TOOLTIP =
  "You don't have access to edit content automations. Please contact your plan administrator to enable this permission.";

export const CONTENT_AUTOMATION_BRAND_ACCESS_REQUIRED_TOOLTIP =
  "You don't have access to some brands in this automation";

export const CONTENT_AUTOMATION_RUN_ERROR_MESSAGE =
  'Failed to run the automation. Please try again.';
